import type { Tag } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, IconButton } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { Select } from '../../components/Field.js'
import { Sheet } from '../../components/Sheet.js'
import { useToast } from '../../components/Toast.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Tags.module.css'

/**
 * Tag management (T-306).
 *
 * Unglamorous and used constantly. Free-text tagging produces near-duplicates
 * within weeks — "snack" and "snacks" — and without merge the only remedy is
 * re-tagging entries by hand, which nobody does, so the tags stop being useful
 * and the filtering built on them stops being trustworthy.
 */
export function TagsScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [tags, setTags] = useState<Tag[] | null>(null)
  const [counts, setCounts] = useState<Map<string, number>>(new Map())
  const [merging, setMerging] = useState<Tag | null>(null)
  const [mergeInto, setMergeInto] = useState('')

  const reload = useCallback(async () => {
    const [all, usage] = await Promise.all([repos.tags.list(), repos.tags.usageCounts()])
    setTags(all)
    setCounts(usage)
  }, [repos])

  useEffect(() => {
    void reload()
  }, [reload])

  async function rename(tag: Tag, name: string) {
    if (name.trim() === '' || name.trim() === tag.name) {
      return
    }
    const result = await repos.tags.rename(tag.id, name.trim())
    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'Could not rename.', tone: 'danger' })
      return
    }
    // Entries reference the tag by id, so nothing about their records changed.
    await reload()
  }

  async function recolour(tag: Tag, colour: string) {
    await repos.tags.update(tag.id, { colour })
    await reload()
  }

  async function remove(tag: Tag) {
    const result = await repos.tags.remove(tag.id)
    await reload()
    if (!result.ok) {
      show({ message: 'Could not delete that.', tone: 'danger' })
      return
    }
    // Detached, not cascaded: losing an entry because a label was tidied up
    // would be absurd (T-107).
    show({
      message:
        result.value.detachedFrom === 0
          ? `Deleted ${tag.name}.`
          : `Deleted ${tag.name}. It was removed from ${String(result.value.detachedFrom)} entries, which are otherwise unchanged.`,
    })
  }

  async function confirmMerge() {
    if (!merging || mergeInto === '') {
      return
    }
    const target = tags?.find((tag) => tag.id === mergeInto)
    const result = await repos.tags.merge(merging.id, mergeInto)
    setMerging(null)
    setMergeInto('')
    await reload()
    if (!result.ok) {
      show({ message: 'Could not merge those.', tone: 'danger' })
      return
    }
    show({
      message: `Moved ${String(result.value.moved)} entries to ${target?.name ?? 'the other tag'}.`,
    })
  }

  if (tags === null) {
    return (
      <>
        <h1>Tags</h1>
        <p>Loading…</p>
      </>
    )
  }

  return (
    <>
      <h1>Tags</h1>

      {tags.length === 0 ? (
        <EmptyState
          title="No tags yet"
          body="Tags mark what was going on around an entry — busy, unwell, travelling. You can create them while logging, and organise them here once there are enough to be worth organising."
        />
      ) : (
        <div className={styles['list']}>
          {tags.map((tag) => {
            const used = counts.get(tag.id) ?? 0
            return (
              <div key={tag.id} className={styles['row']}>
                <input
                  type="color"
                  className={styles['colour']}
                  value={tag.colour ?? '#8b939e'}
                  aria-label={`Colour for ${tag.name}`}
                  onChange={(event) => void recolour(tag, event.target.value)}
                />
                <span className={styles['body']}>
                  <input
                    className={styles['name']}
                    defaultValue={tag.name}
                    aria-label={`Name of ${tag.name}`}
                    // On blur rather than per keystroke: renaming to a name
                    // that momentarily clashes with another tag would fail
                    // halfway through typing it.
                    onBlur={(event) => void rename(tag, event.target.value)}
                  />
                  <span className={styles['usage']}>
                    {used === 0
                      ? 'Not used yet'
                      : used === 1
                        ? 'On 1 entry'
                        : `On ${String(used)} entries`}
                  </span>
                </span>
                <span className={styles['actions']}>
                  {used > 0 && (
                    <IconButton
                      label={`Show entries tagged ${tag.name}`}
                      onClick={() => void navigate(`/timeline?tag=${tag.id}`)}
                    >
                      <Search />
                    </IconButton>
                  )}
                  {tags.length > 1 && (
                    <IconButton
                      label={`Merge ${tag.name} into another tag`}
                      onClick={() => setMerging(tag)}
                    >
                      <Merge />
                    </IconButton>
                  )}
                  <IconButton
                    label={`Delete ${tag.name}`}
                    onClick={() => void remove(tag)}
                  >
                    <Bin />
                  </IconButton>
                </span>
              </div>
            )
          })}
        </div>
      )}

      <Sheet
        open={merging !== null}
        title={`Merge ${merging?.name ?? ''}`}
        onClose={() => {
          setMerging(null)
          setMergeInto('')
        }}
      >
        <div className={styles['merge']}>
          <p>
            Every entry tagged <strong>{merging?.name}</strong> will be tagged with the
            one you pick instead, and <strong>{merging?.name}</strong> will be deleted. No
            entries are removed.
          </p>
          <Select
            label="Merge into"
            value={mergeInto}
            onChange={(event) => setMergeInto(event.target.value)}
          >
            <option value="">Choose a tag</option>
            {(tags ?? [])
              .filter((tag) => tag.id !== merging?.id)
              .map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
          </Select>
          <div className={styles['mergeActions']}>
            <Button onClick={() => setMerging(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={mergeInto === ''}
              onClick={() => void confirmMerge()}
            >
              Merge
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}

function Search() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M9 15a6 6 0 1 1 0-12 6 6 0 0 1 0 12zM13.5 13.5L17 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function Merge() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M5 3v4a4 4 0 0 0 4 4h6M15 3v4a4 4 0 0 1-4 4H9m6 0-3-3m3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Bin() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
